import {getCurrentUserInfo} from '@api/user';
import Context from '@context/Context';
import {isApiError} from '@utils/errorUtils';
import type {CurrentUserInfo} from '@interfaces/Api';
import {useEffect, useState} from 'react';

interface CurrentUserState extends Partial<CurrentUserInfo> {
    isRetrieved: boolean;
    isError: boolean;
    isLoggedIn: boolean;
}

class CurrentUserContext extends Context<CurrentUserState> {
    public readonly actions = {
        fillUserInfo: 'fillUserInfo',
    };

    public constructor(initialState: CurrentUserState) {
        super(initialState);
        void this.dispatch(this.actions.fillUserInfo);
    }

    protected reduce(action: string): Promise<CurrentUserState> | CurrentUserState {
        switch (action) {
            case this.actions.fillUserInfo:
                if (!this.state.isRetrieved && !this.state.isError) {
                    return getCurrentUserInfo()
                        .then(response => ({
                            isRetrieved: true,
                            isError: false,
                            isLoggedIn: true,
                            ...response.data,
                        }))
                        .catch((error: unknown) => {
                            if (isApiError(error)) {
                                // this means we received 4xx error, so most likely did not pass auth (which means there is no logged in user)
                                return {isRetrieved: true, isError: false, isLoggedIn: false};
                            }
                            // this means we received 5xx error, which means there was either a code issue or a timeout
                            return {isRetrieved: false, isError: true, isLoggedIn: false};
                        });
                }
                return this.state;
            default:
                throw new Error('Unknown action');
        }
    }
}

const context = new CurrentUserContext({
    isRetrieved: false,
    isError: false,
    isLoggedIn: false,
});
export default context;

export function useCurrentUserContext(): CurrentUserState {
    const [currentState, setCurrentState] = useState(context.currentState);

    function updateLocalState(store: CurrentUserState) {
        setCurrentState(store);
    }

    useEffect(() => {
        context.registerListener(updateLocalState);
        return () => context.deregisterListener(updateLocalState);
    }, []);

    return currentState;
}
